import {ErrorMessage, Form, Formik} from 'formik'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import Modal from 'rsuite/Modal'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import * as Yup from 'yup'

import {isEmpty} from 'lodash'
import {StateParamsModel} from 'src/app/modules/common/Model'
import FormRadio from 'src/cms/helpers/components/forms/FormRadio'
import FormSelect from 'src/cms/helpers/components/forms/FormSelect'
import {service as fdTypeService} from '../../fdType/redux/service'
import * as fdTenure from '../index'

type Props = {
  open: boolean
  params?: StateParamsModel
  handleClose: () => void
  actionType: string
  editSelectedData?: any
}

const FORM_VALIDATION = Yup.object().shape({
  tenure: Yup.number()
    .typeError('Tenure must be a number')
    .required('Tenure is required'),
  tenure_by: Yup.string().required('Tenure By is required'),
  minimum_balance: Yup.number()
    .typeError('Minimum Balance must be a number')
    .required('Minimum Balance is required'),
  fixed_deposit_products: Yup.array()
    .min(1, 'At least one product is required')
    .required('Products are required'),
  status: Yup.string().required('Status is required'),
})

const AddFdTenure = ({open, params, handleClose, actionType, editSelectedData}: Props) => {
  const dispatch = useDispatch()
  const {loading, success} = useSelector((state: any) => state.fdTenure)

  const [fdTypeList, setFdTypeList] = useState<any[]>([])

  useEffect(() => {
    fdTypeService
      .getFdTypeList()
      .then((res: any) => {
        setFdTypeList(res?.data?.data || [])
      })
      .catch(() => {
        setFdTypeList([])
      })
  }, [])

  const statusOptions = [
    {label: 'Active', value: 'active'},
    {label: 'Inactive', value: 'inactive'},
  ]

  const tenureByOptions = [
    {label: 'Months', value: 'months'},
    {label: 'Years', value: 'years'},
  ]

  const fdTypeOptions = fdTypeList?.map((item: any) => ({
    label: item.title,
    value: item.id,
  }))

  useEffect(() => {
    if (success) {
      dispatch(fdTenure?.actions.getFdTenure(params))
      isEmpty(editSelectedData)
        ? toast.success('FD Tenure added successfully')
        : toast.success('FD Tenure edited successfully')
      dispatch(fdTenure?.actions?.addFdTenureReset())
      handleClose()
    }
  }, [success])

  return (
    <div className='modal-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        keyboard={false}
        enforceFocus={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>{actionType} FD Tenure</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              initialValues={{
                tenure: '',
                tenure_by: 'months',
                minimum_balance: '',
                fixed_deposit_products: [],
                gl_subhead_code: '',
                status: 'active',
              }}
              validationSchema={FORM_VALIDATION}
              onSubmit={(values, {}) => {
                const formData = {
                  ...values,
                }

                if (!isEmpty(editSelectedData)) {
                  dispatch(fdTenure.actions.updateFdTenure(formData, editSelectedData?.id))
                } else {
                  dispatch(fdTenure.actions.addFdTenure(formData))
                }
              }}
            >
              {({isSubmitting, touched, handleChange, errors, values, setFieldValue}) => {
                useEffect(() => {
                  if (!isEmpty(editSelectedData)) {
                    const fields = [
                      'tenure',
                      'tenure_by',
                      'minimum_balance',
                      'fixed_deposit_products',
                      'gl_subhead_code',
                      'status',
                    ]
                    fields.forEach((field) =>
                      setFieldValue(field, editSelectedData[field] ?? '', false)
                    )

                    setFieldValue(
                      'fixed_deposit_products',
                      editSelectedData?.fixed_deposit_products ?? [],
                      false
                    )
                  }
                }, [])

                return (
                  <Form>
                    <div className='card-body border-top pt-5'>
                      <div className='row'>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='number'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Tenure'
                            label='Tenure'
                            name='tenure'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Please select tenure by'
                            label='Tenure By'
                            name='tenure_by'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            options={tenureByOptions}
                            required={true}
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='number'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Minimum Balance'
                            label='Minimum Balance'
                            name='minimum_balance'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='GL Subhead Code'
                            label='GL Subhead Code'
                            name='gl_subhead_code'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>

                        <div className='col-md-12 col-xs-12'>
                          <FormSelect
                            multiple={true}
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Please select fixed deposit products'
                            label='Fixed Deposit Products'
                            name='fixed_deposit_products'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            options={fdTypeOptions}
                            required={true}
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </div>

                        <div className='col-md-12 mt-5 col-xs-12'>
                          <label className='form-label fw-bolder text-dark fs-6 required'>
                            Status
                          </label>

                          <div className='d-flex ms-5'>
                            {statusOptions?.map((status: {label: string; value: string}) => (
                              <FormRadio
                                containerClassName=''
                                label='Select Status'
                                name='status'
                                type='radio'
                                onChange={handleChange}
                                errors={errors}
                                touched={touched}
                                required={true}
                                checkBoxText={status?.label}
                                value={status?.value}
                              />
                            ))}
                          </div>
                          <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
                            <ErrorMessage
                              name='status'
                              component='div'
                              className='field-error-message'
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex justify-content-end px-9 '>
                      <button
                        type='submit'
                        disabled={loading}
                        className='btn btn-primary btn-sm ms-3'
                        data-cy='modal-submit'
                      >
                        Save
                      </button>
                      <button
                        type='button'
                        onClick={handleClose}
                        className='btn btn-secondary btn-sm ms-3'
                        data-cy='modal-cancel'
                      >
                        Cancel
                      </button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
export default AddFdTenure
